import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const img = (id) => `https://images.unsplash.com/${id}?q=85&w=1400&auto=format&fit=crop`;

const IMG_PIPES = "/ms-pipes.png";
const IMG_ANGLES = "/ms-angles.png";
const IMG_TMT = "/tmt-rods.png";
const IMG_CHANNELS = "/ms-channels.png";
const IMG_FLATS = "/ms-flats.png";
const IMG_GI = "/gi-items.png";
const IMG_IRON_SCRAP = img("photo-1722695510527-cc033e43be1b");
const IMG_PLASTIC = img("photo-1595278069441-2cf29f8005a4");
const IMG_VEHICLE = img("photo-1693925380766-830b8062495d");

const tiles = [
  {
    name: "MS Pipes & Square Pipes",
    specs: ["Round / Square / Rectangular", "All standard gauges & lengths", "Cut-to-size available"],
    img: IMG_PIPES,
    span: "md:col-span-8",
    tall: true,
  },
  {
    name: "MS Angles",
    specs: ["25×25 to 100×100", "Equal & unequal angles"],
    img: IMG_ANGLES,
    span: "md:col-span-4",
  },
  {
    name: "TMT Rods",
    specs: ["Fe 500 / Fe 550", "8mm – 32mm"],
    img: IMG_TMT,
    span: "md:col-span-4",
  },
  {
    name: "MS Channels",
    specs: ["75×40 to 400×100", "ISMC standard"],
    img: IMG_CHANNELS,
    span: "md:col-span-4",
  },
  {
    name: "MS Flats",
    specs: ["20mm – 100mm widths", "All thicknesses"],
    img: IMG_FLATS,
    span: "md:col-span-4",
  },
  {
    name: "GI Items",
    specs: ["GI pipes, sheets & fittings", "Galvanised, rust-resistant"],
    img: IMG_GI,
    span: "md:col-span-4",
  },
  {
    name: "Iron Scrap",
    specs: ["We buy & we sell", "Best market rates, honest scale"],
    img: IMG_IRON_SCRAP,
    span: "md:col-span-4",
  },
  {
    name: "Plastic Scrap",
    specs: ["Bottles, cans & industrial plastic", "Bulk lots picked up"],
    img: IMG_PLASTIC,
    span: "md:col-span-4",
  },
  {
    name: "Vehicle Scrap",
    specs: ["Cars, bikes & commercial vehicles", "Fair value, on-the-spot payment", "Complete paperwork handled"],
    img: IMG_VEHICLE,
    span: "md:col-span-12",
  },
];

export default function Products() {
  return (
    <section id="products" className="border-b border-[#262626]" data-testid="products-section">
      <div className="px-6 md:px-12 pt-32 md:pt-48 pb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="font-display text-4xl sm:text-6xl lg:text-8xl uppercase tracking-tight leading-none text-steel"
        >
          What We Stock
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="text-xs uppercase tracking-[0.25em] text-[#525252] max-w-xs"
        >
          New steel, full range /// Scrap, both directions
        </motion.p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-px bg-[#262626] border-t border-[#262626]">
        {tiles.map((t, i) => (
          <motion.button
            key={t.name}
            onClick={() => scrollToSection("#quote")}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
            className={`group relative bg-[#050505] text-left overflow-hidden hover:bg-[#121212] transition-colors duration-500 ${t.span} ${t.tall ? "min-h-[420px]" : "min-h-[340px]"}`}
            data-testid={`product-tile-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            <div className="absolute inset-0">
              <img src={t.img} alt={t.name} className="spotlight-img w-full h-full object-cover opacity-40" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-[#050505]/20" />
            </div>
            <div className="relative h-full flex flex-col justify-between p-8 md:p-10">
              <div className="flex items-start justify-between">
                <span className="text-xs uppercase tracking-[0.25em] text-[#525252]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <ArrowUpRight
                  size={20}
                  className="text-[#525252] group-hover:text-[#f5f5f5] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300"
                />
              </div>
              <div>
                <h3 className="font-display text-2xl sm:text-3xl uppercase text-steel-soft mb-4">{t.name}</h3>
                <ul className="space-y-1">
                  {t.specs.map((s) => (
                    <li key={s} className="text-xs md:text-sm text-[#a3a3a3]">
                      — {s}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.button>
        ))}
      </div>
    </section>
  );
}
